import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { loginUser, loginWithSocial } from '../../redux/actions/loginAction';

import { resetLoginState, setSocialUser } from '../../redux/slices/loginSlice';
import { useDarkMode } from '../../App';
import { signInWithGoogle } from '../../firebase/firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight, FiSmartphone, FiGlobe } from 'react-icons/fi';
import { FaGoogle } from 'react-icons/fa';
import './Login.css';
import { log } from 'three/src/utils.js';

const LanguageSwitcher = () => {
  const { i18n } = useTranslation('global');
  const isAR = i18n.language === 'ar';

  const toggleLanguage = () => {
    const newLang = isAR ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    localStorage.setItem('lang', newLang);
  };

  return (
    <motion.button
      className="auth-lang-switcher"
      onClick={toggleLanguage}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      title={isAR ? 'Switch to English' : 'التبديل إلى العربية'}
    >
      <FiGlobe />
      <span>{isAR ? 'English' : 'العربية'}</span>
    </motion.button>
  );
};

const Login = () => {
  const { t, i18n } = useTranslation('global');
  const { isDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loginState = useSelector((state) => state.login);
  const { isLoading, success, error, data } = loginState || {};
  console.log({data , success , error});

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSocialLoading, setIsSocialLoading] = useState({ google: false });

  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = t('login.title', 'Login');
  }, [t]);

  useEffect(() => {
    if (success) {
      if (data?.message === "Email not verified. OTP sent." || data?.message?.includes("Email not verified")) {
        toast.info(data?.message);
        const email = data?.data?.email || formData.email;
        dispatch(resetLoginState());
        alert(email)
        navigate('/verify-otp', { state: { email } });
        return;
      }

      toast.success(data?.message || t('login.successMessage', 'Login successful!'));
      const user = data?.data?.user || data?.user || null;
      const isActive = user?.is_active ?? user?.active ?? user?.isVerified ?? user?.is_verified ?? user?.verified ?? user?.email_verified ?? false;
      const emailToPass = formData.email || user?.email || '';

      dispatch(resetLoginState());
      setTimeout(() => {
        navigate('/');
      }, 800);
    }
  }, [success, data, t, navigate, dispatch, formData.email]);

  useEffect(() => {
    if (error) {
      if (typeof error === 'object' && error.status === 410) {
        toast.info(error.message);
        const email = error.data?.email || formData.email;
        dispatch(resetLoginState());
        navigate('/verify-otp', { state: { email } });
        return;
      }
      
      toast(error);
      dispatch(resetLoginState());
    }
  }, [error, dispatch, formData.email, navigate]);

  useEffect(() => {
    return () => {
      dispatch(resetLoginState());
    };
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.email) {
      toast(t('login.emailRequired', 'Email is required'));
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast(t('login.emailInvalid', 'Please enter a valid email'));
      return false;
    }

    if (!formData.password) {
      toast(t('login.passwordRequired', 'Password is required'));
      return false;
    }

    if (formData.password.length < 8) {
      toast(t('login.passwordMinLength', 'Password must be at least 8 characters'));
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (validateForm()) {
      dispatch(loginUser({ ...formData, lang: i18n.language }));
    }
  };

  const handleGoogleLogin = async () => {
    if (isSocialLoading.google) return;
    setIsSocialLoading(prev => ({ ...prev, google: true }));
    try {
      const result = await signInWithGoogle();
      if (result.success) {
        toast.success(t('login.googleSuccess', 'Logged in successfully with Google!'));
        const token = await result.user.getIdToken();
        dispatch(loginWithSocial({ 
          id_token: token, 
          device_name: 'mobile',
          lang: i18n.language 
        }));


      } else {
        // Handle specific Firebase error for cancelled popups to avoid "Internal Assertion" confusion
        if (result.code !== 'auth/popup-closed-by-user') {
          toast(result.error || t('login.googleError', 'Failed to login with Google'));
        }
      }
    } catch (error) {
      toast(t('login.googleError', 'Failed to login with Google'));
    } finally {
      setIsSocialLoading(prev => ({ ...prev, google: false }));
    }
  };


  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' } 
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.5, ease: 'easeOut' } 
    }
  };

  return (
    <div className={`login-page ${isDarkMode ? 'login-page--dark' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Background Decor */}
      <div className="login-bg-overlay"></div>
      <div className="login-blobs">
        <div className="blob"></div>
        <div className="blob"></div>
        <div className="blob"></div>
      </div>

      <LanguageSwitcher />

      <motion.div 
        className="login-container"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div className="login-card main-glass" variants={itemVariants}>
          <div className="login-brand">
            <motion.div 
              className="brand-circle"
              whileHover={{ scale: 1.05, rotate: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <FiSmartphone className="brand-icon-svg" />
            </motion.div>
            <motion.h1 variants={itemVariants}>
              {t('login.welcomeBack', 'Welcome Back')}
            </motion.h1>
          </div>

          <div className="login-header">
            <motion.h2 variants={itemVariants}>
              {t('login.headerTitle', 'OnePlus Login')}
            </motion.h2>
            <motion.p variants={itemVariants}>
              {t('login.subtitle', 'Premium Access to Your Tech Ecosystem')}
            </motion.p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            <motion.div className="input-group-modern" variants={itemVariants}>
              <label htmlFor="email">
                <FiMail className="input-icon" />
                {t('login.email', 'Email Address')}
              </label>
              <div className="input-relative">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('login.emailPlaceholder', 'your.email@example.com')}
                  className={submitted && !formData.email ? 'error' : ''}
                />
                <div className="input-glow"></div>
              </div>
            </motion.div>

            <motion.div className="input-group-modern" variants={itemVariants}>
              <label htmlFor="password">
                <FiLock className="input-icon" />
                {t('login.password', 'Password')}
              </label>
              <div className="password-wrapper-modern">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('login.passwordPlaceholder', 'Enter your password')}
                  className={submitted && !formData.password ? 'error' : ''}
                />
                <button 
                  type="button" 
                  className="toggle-password-modern" 
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
                <div className="input-glow"></div>
              </div>
            </motion.div>

            <motion.div className="forgot-password-link" variants={itemVariants}>
              <Link to="/forgot-password">{t('login.forgotPassword', 'Forgot Password?')}</Link>
            </motion.div>

            <motion.button 
              type="submit" 
              className="submit-btn-modern" 
              disabled={isLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              variants={itemVariants}
            >
              {isLoading ? (
                <span className="spinner"></span>
              ) : (
                <>
                  {t('login.loginButton', 'Login')}
                  <FiArrowRight className="btn-arrow" />
                </>
              )}
            </motion.button>

            <motion.div className="register-link" variants={itemVariants}>
              <p>
                {t('login.dontHaveAccount', "Don't have an account?")}{' '}
                <Link to="/createaccount" className="highlight-link">
                  {t('login.registerNow', 'Register Now')}
                </Link>
              </p>
            </motion.div>

            <motion.div className="divider-modern" variants={itemVariants}>
              <span>{t('login.or', 'or continue with')}</span>
            </motion.div>

            <motion.div className="social-login-grid" variants={itemVariants}>
              <button 
                type="button" 
                className={`social-btn-modern google ${isSocialLoading.google ? 'loading' : ''}`} 
                onClick={handleGoogleLogin}
                disabled={isSocialLoading.google || isLoading}
              >
                {isSocialLoading.google ? <span className="auth-spinner-small"></span> : <FaGoogle />}
                <span>Google</span>
              </button>
            </motion.div>
          </form>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Login;

